import { Box, Theme } from "@outschool/backpack";
import { UserMode } from "@outschool/routes";
import { CurrentUser, useSession } from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import { Redirect } from "@outschool/ui-utils";
import React, { useState } from "react";

import {
  getOrgAdminRoutesFromUser,
  isBuyerOrgAdmin,
} from "../../../lib/BuyerOrgAdmins";
import { useLastUserMode, userModeRootPath } from "../../../lib/UserMode";
import SimpleLayout from "../../layouts/SimpleLayout";
import { HEADER_WIDTH } from "../../nav/Header";
import DashboardNav from "../components/DashboardNav";
import LearnerBanner from "../components/LearnerBanner";
import NextMeetingBanner from "../components/NextMeetingBanner";
import { useParentDashboardRedirects } from "../hooks/useParentDashboardRedirects";

function RedirectToOrgAdmin({ user }: { user: CurrentUser }) {
  const { homeRoute } = getOrgAdminRoutesFromUser(user);

  return <Redirect to={homeRoute} />;
}

export default function ParentDashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { stripeIsLoading } = useParentDashboardRedirects();
  const { currentUser, currentUserIsLoading } = useSession();
  const [lastUserMode] = useLastUserMode();
  const buyerOrgAdminModeEnabled = useFeatureFlag(
    "partners-buyer-org-admin-mode",
  );
  const [learnerBannerHeight, setLearnerBannerHeight] = useState(0);

  const loading =
    currentUser === undefined || currentUserIsLoading || stripeIsLoading;

  if (lastUserMode === UserMode.Educator) {
    return <Redirect to={userModeRootPath(lastUserMode)} />;
  } else if (buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser)) {
    return <RedirectToOrgAdmin user={currentUser} />;
  }

  return (
    <Box sx={{ backgroundColor: "grey.50" }}>
      <SimpleLayout allowCategoriesNavbar>
        {loading ? (
          <Loading />
        ) : (
          <>
            <LearnerBanner setLearnerBannerHeight={setLearnerBannerHeight} />
            <Box
              sx={(theme: Theme) => ({
                py: 24,
                display: "grid",
                gridTemplateColumns: "236px 1fr",
                gap: 30,
                maxWidth: HEADER_WIDTH,
                margin: "0 auto",
                "@media (max-width: 1440px)": {
                  px: 24,
                },
                [theme.breakpoints.down("md")]: {
                  display: "block",
                  padding: 0,
                },
              })}
            >
              <DashboardNav learnerBannerHeight={learnerBannerHeight} />
              <Box sx={{ minWidth: 0 }}>{children}</Box>
            </Box>
            <NextMeetingBanner />
          </>
        )}
      </SimpleLayout>
    </Box>
  );
}
